/**
 * Bulk messaging: pacing, personalisation and credits.
 *
 * The hard part of bulk WhatsApp is not sending — it is sending slowly enough. WhatsApp
 * scores every number on behaviour, and a burst of identical messages to strangers is the
 * clearest possible signal of a bot. A banned number takes the customer's whole operation
 * down with it, so the pacing rules here are deliberately conservative and the product
 * refuses sends it believes are reckless rather than letting the customer find out later.
 */

/** How long a number has been in use with us, which is what earns it a tighter cadence. The
 *  daily ceiling is a separate, finer-grained question and lives in `warmup.ts`. */
export type NumberMaturity = 'new' | 'warming' | 'established'

export function maturityFor(connectedAt: Date | null, now = new Date()): NumberMaturity {
  if (!connectedAt) return 'new'
  const days = (now.getTime() - connectedAt.getTime()) / 86_400_000
  if (days < 3) return 'new'
  if (days < 10) return 'warming'
  return 'established'
}

/**
 * What one number may do in this run: how many conversations it may start today, and how
 * fast. The cap comes from `warmup.ts`, which owns the ramp and the health adjustment; this
 * module only spends it. Both are needed together and travel together, because a plan built
 * from one number's cap and another's cadence would be nonsense nothing would catch.
 */
export interface NumberPacing {
  maturity: NumberMaturity
  /** Conversations this number may start today. Replies are never rate limited. */
  cap: number
}

/** Seconds between sends. A fixed cadence is itself a signature, so the range is wide. */
const GAP_SECONDS: Record<NumberMaturity, { min: number; max: number }> = {
  new: { min: 90, max: 240 },
  warming: { min: 45, max: 150 },
  established: { min: 20, max: 75 },
}

/** The tightest spacing a number of this maturity is ever planned at. */
export function minGapSeconds(maturity: NumberMaturity): number {
  return GAP_SECONDS[maturity].min
}

/**
 * Whether a number may start another conversation right now, judged on the clock rather than
 * on the plan.
 *
 * The offsets `planSend` produces describe a cadence only while a run is on schedule. The
 * moment one falls behind — a number that was down for an hour, a `retry-failed` that resets
 * every offset to zero, a host that was restarted — every remaining offset is in the past and
 * "due" stops meaning anything. The drain would then hand its whole remaining audience over
 * and the host would send it at its own settle rate, turning a fortnight of deliberate spacing
 * into a couple of minutes. That is the exact signature the pacing exists to avoid, and it is
 * reachable from three different directions, so the guard is on the outcome rather than on any
 * one of them: a number does not start a second conversation sooner than its own plan would
 * ever have scheduled one.
 *
 * The floor is the minimum of the maturity's gap range, not a fresh draw, so this can never
 * hold back a run that is keeping to its schedule — consecutive planned offsets are at least
 * this far apart by construction.
 */
export function dueToSend(
  maturity: NumberMaturity,
  lastSentAt: Date | null,
  now: Date = new Date(),
): boolean {
  if (!lastSentAt) return true
  return (now.getTime() - lastSentAt.getTime()) / 1000 >= minGapSeconds(maturity)
}

export interface PacingPlan {
  /** Recipients this run will actually attempt. */
  scheduled: number
  /** Recipients deferred because they exceed today's safe ceiling. */
  deferred: number
  /** Seconds from start at which each scheduled recipient is sent. */
  offsets: number[]
  estimatedMinutes: number
  cap: number
  maturity: NumberMaturity
  warning: string | null
}

/**
 * Builds a send schedule.
 *
 * `random` is injected so the jitter is reproducible under test — the property that matters
 * is that gaps vary, not which specific values come out.
 */
export function planSend(
  recipientCount: number,
  pacing: NumberPacing,
  alreadySentToday: number,
  random: () => number = Math.random,
): PacingPlan {
  const { maturity, cap } = pacing
  const remaining = Math.max(0, cap - alreadySentToday)
  const scheduled = Math.min(recipientCount, remaining)
  const deferred = recipientCount - scheduled

  const gap = GAP_SECONDS[maturity]
  const offsets: number[] = []
  let cursor = 0
  for (let i = 0; i < scheduled; i++) {
    offsets.push(Math.round(cursor))
    cursor += gap.min + random() * (gap.max - gap.min)
  }

  let warning: string | null = null
  if (deferred > 0 && cap === 0) {
    // A zero cap is the health tier refusing the number entirely, not a ceiling it has spent.
    warning =
      `This number should not start new conversations right now, so all ${deferred} are held ` +
      'for the next run.'
  } else if (deferred > 0) {
    warning =
      `This number has a safe limit of ${cap} new conversations a day. ` +
      `${scheduled} will go today and ${deferred} will carry over, so the number is not put at risk.`
  } else if (maturity === 'new' && recipientCount > 10) {
    warning =
      'This number is less than three days old. Sending to strangers before it has real ' +
      'two-way conversations behind it is the fastest way to get it banned.'
  }

  return {
    scheduled,
    deferred,
    offsets,
    estimatedMinutes: offsets.length > 0 ? Math.ceil(offsets[offsets.length - 1] / 60) : 0,
    cap,
    maturity,
    warning,
  }
}

/**
 * Spreads a send across several numbers.
 *
 * Splitting the list is how a large send stays inside per-number ceilings, and it also
 * keeps any single number from carrying the whole risk of a campaign.
 */
export function splitAcrossNumbers<T>(
  recipients: T[],
  numbers: { id: string; pacing: NumberPacing; sentToday: number }[],
): { numberId: string; recipients: T[]; plan: PacingPlan }[] {
  if (numbers.length === 0) return []

  const capacity = numbers.map((n) => ({
    ...n,
    room: Math.max(0, n.pacing.cap - n.sentToday),
    taken: [] as T[],
  }))

  // Filled in descending order of remaining room so the healthiest number absorbs the most,
  // rather than a round robin that would push a new number to its ceiling first.
  const queue = [...recipients]
  let progress = true
  while (queue.length > 0 && progress) {
    progress = false
    for (const slot of [...capacity].sort((a, b) => b.room - a.room)) {
      if (queue.length === 0) break
      if (slot.taken.length >= slot.room) continue
      slot.taken.push(queue.shift()!)
      progress = true
    }
  }

  return capacity
    .filter((slot) => slot.taken.length > 0)
    .map((slot) => ({
      numberId: slot.id,
      recipients: slot.taken,
      plan: planSend(slot.taken.length, slot.pacing, slot.sentToday),
    }))
}

/** Narrows to the numbers a send was scoped to, if any were chosen — `null`/empty means every
 *  connected number, the behaviour before a send could name specific ones. */
export function filterToChosenNumbers<T extends { id: string }>(
  numbers: T[],
  numberIds: string[] | null,
): T[] {
  if (!numberIds || numberIds.length === 0) return numbers
  const wanted = new Set(numberIds)
  return numbers.filter((n) => wanted.has(n.id))
}

/* ---------------------------------------------------------------- recurrence */

export type Recurrence = 'daily' | 'weekly' | 'monthly'

/** Days in a given month, so a month-end date can be clamped rather than rolling over. */
function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate()
}

/**
 * Advances a schedule by one interval (or `every` of them — "every 2 weeks" passes 2).
 *
 * Monthly is the awkward one: adding a month to 31 January lands on 3 March in naive date
 * maths, silently moving the send off the month end the customer chose. The day is clamped
 * to the shorter month instead, and — importantly — the *intended* day is carried forward
 * so a 31st schedule returns to the 31st rather than sticking at 28 forever.
 *
 * `weekdays` (0=Sunday..6=Saturday, matching `getUTCDay()`) picks specific days for a weekly
 * send — "every Mon/Wed/Fri" — by stepping a day at a time to the next matching day instead of
 * jumping a flat 7 days. When set, `every` is not meaningful (there is no "every 2 weeks on
 * Mon/Wed/Fri" here) and is ignored rather than combined into undefined behaviour; the caller
 * is expected not to offer both controls at once.
 */
export function addInterval(
  from: Date,
  recurrence: Recurrence,
  intendedDay?: number,
  every = 1,
  weekdays?: number[],
): Date {
  if (recurrence === 'daily') return new Date(from.getTime() + every * 86_400_000)
  if (recurrence === 'weekly') {
    if (weekdays && weekdays.length > 0) {
      // A day is always in the future: the occurrence at `from` has already happened, so even
      // a day whose weekday is in the set is not a valid next slot until it comes back around.
      for (let step = 1; step <= 7; step++) {
        const candidate = new Date(from.getTime() + step * 86_400_000)
        if (weekdays.includes(candidate.getUTCDay())) return candidate
      }
    }
    return new Date(from.getTime() + every * 7 * 86_400_000)
  }

  const year = from.getUTCFullYear()
  const month = from.getUTCMonth()
  const wanted = intendedDay ?? from.getUTCDate()

  const nextMonth = month + every
  const targetYear = year + Math.floor(nextMonth / 12)
  const targetMonth = nextMonth % 12
  const day = Math.min(wanted, daysInMonth(targetYear, targetMonth))

  return new Date(
    Date.UTC(
      targetYear,
      targetMonth,
      day,
      from.getUTCHours(),
      from.getUTCMinutes(),
      from.getUTCSeconds(),
    ),
  )
}

export interface NextOccurrence {
  at: Date
  /** Occurrences that elapsed while nothing was running, and were deliberately not sent. */
  skipped: number
}

/**
 * The next time a recurring send should fire.
 *
 * Computed from the previous *scheduled* time rather than when it actually completed, so a
 * send that ran late does not drag the whole series later and later.
 *
 * If the host was offline across several intervals, the missed ones are skipped rather than
 * fired as a backlog: a daily campaign that suddenly sends seven messages to each contact
 * would get the number banned, which is exactly what this feature exists to avoid.
 */
export function nextOccurrence(
  previousScheduledFor: Date,
  recurrence: Recurrence,
  now = new Date(),
  every = 1,
  weekdays?: number[],
): NextOccurrence {
  const intendedDay = previousScheduledFor.getUTCDate()
  let at = addInterval(previousScheduledFor, recurrence, intendedDay, every, weekdays)
  let skipped = 0

  // Guarded rather than while(true): a pathological clock should not spin here.
  const MAX_CATCH_UP = 500
  while (at <= now && skipped < MAX_CATCH_UP) {
    at = addInterval(at, recurrence, intendedDay, every, weekdays)
    skipped += 1
  }

  return { at, skipped }
}

/* ---------------------------------------------------------------- variables */

export interface VariableDef {
  name: string
  /** Used wherever a recipient has no value, so a message never ships with a raw token. */
  fallback: string
}

/** Finds `{name}` tokens in a message body. */
export function extractVariables(body: string): string[] {
  return [...new Set([...body.matchAll(/\{([a-zA-Z0-9_ -]+)\}/g)].map((m) => m[1].trim()))]
}

/**
 * Renders a message for one recipient.
 *
 * A blank value falls back rather than leaving a gap, because "Hi ," reads worse than a
 * generic greeting and is an obvious tell that the message was automated.
 */
export function renderMessage(
  body: string,
  values: Record<string, string>,
  defs: VariableDef[] = [],
): string {
  const fallbacks = new Map(defs.map((d) => [d.name, d.fallback]))
  return body.replace(/\{([a-zA-Z0-9_ -]+)\}/g, (_, raw: string) => {
    const name = raw.trim()
    const value = values[name]?.trim()
    if (value) return value
    return fallbacks.get(name) ?? ''
  })
}

/** Every recipient still missing a value for a variable that has no fallback. */
export function missingValues(
  body: string,
  defs: VariableDef[],
  recipients: { id: string; variables: Record<string, string> }[],
): { recipientId: string; variable: string }[] {
  const needed = extractVariables(body)
  const withFallback = new Set(defs.filter((d) => d.fallback.trim()).map((d) => d.name))
  const gaps: { recipientId: string; variable: string }[] = []

  for (const recipient of recipients) {
    for (const name of needed) {
      if (withFallback.has(name)) continue
      if (!recipient.variables[name]?.trim()) {
        gaps.push({ recipientId: recipient.id, variable: name })
      }
    }
  }
  return gaps
}

/* ---------------------------------------------------------------- credits */

/** One credit per message. Monthly allowance is consumed before purchased topup. */
export const MONTHLY_CREDITS_PER_SEAT = 3000
export const TOPUP_PRICE_MINOR_PER_CREDIT = 0.6

export interface CreditBalance {
  monthlyRemaining: number
  topupRemaining: number
}

export function totalCredits(balance: CreditBalance): number {
  return balance.monthlyRemaining + balance.topupRemaining
}

export interface CreditSpend {
  fromMonthly: number
  fromTopup: number
  short: number
}

/**
 * Works out how a send is paid for. Monthly first, because it expires at the period end
 * while purchased topup rolls over — spending topup first would quietly waste the
 * allowance the customer already paid for.
 */
export function spendCredits(balance: CreditBalance, messages: number): CreditSpend {
  const fromMonthly = Math.min(balance.monthlyRemaining, messages)
  const fromTopup = Math.min(balance.topupRemaining, messages - fromMonthly)
  return { fromMonthly, fromTopup, short: messages - fromMonthly - fromTopup }
}

export function monthlyAllowanceFor(seats: number, billableNumbers: number): number {
  // The first number is bundled with the workspace and does not earn credits.
  return (seats + Math.max(0, billableNumbers - 1)) * MONTHLY_CREDITS_PER_SEAT
}
